/**
 * API de agenda del profesional: reglas, bloqueos, slots libres.
 */
import { apiFetch } from "./client";

export interface AgendaRegla {
  id: number;
  profesional_id: number;
  sede_id: number;
  sede_nombre: string;
  dia_semana: number; // 0=Lunes ... 6=Domingo
  hora_inicio: string; // HH:MM:SS
  hora_fin: string;
  duracion_min: number;
  activo: boolean;
  nota: string | null;
}

export interface AgendaReglaCreate {
  sede_id: number;
  dia_semana: number;
  hora_inicio: string;
  hora_fin: string;
  duracion_min: number;
  activo?: boolean;
  nota?: string | null;
}

export type AgendaReglaUpdate = Partial<AgendaReglaCreate>;

export interface AgendaBloqueo {
  id: number;
  profesional_id: number;
  fecha_inicio: string;
  fecha_fin: string;
  /** null en ambas = día completo; con valor = solo esa franja. */
  hora_inicio: string | null;
  hora_fin: string | null;
  motivo: string | null;
}

export interface AgendaBloqueoCreate {
  fecha_inicio: string;
  fecha_fin: string;
  /** Omitir ambas bloquea el día completo. Con horas, debe ser un solo día. */
  hora_inicio?: string | null;
  hora_fin?: string | null;
  motivo?: string | null;
}

export interface SlotLibre {
  profesional_id: number;
  sede_id: number;
  sede_nombre: string;
  inicio: string;
  fin: string;
  duracion_min: number;
}

// ---------- Reglas ----------

export function listReglas(profesionalId: number): Promise<AgendaRegla[]> {
  return apiFetch<AgendaRegla[]>(
    `/v1/profesionales/${profesionalId}/agenda/reglas`,
  );
}

export function createRegla(
  profesionalId: number,
  payload: AgendaReglaCreate,
): Promise<AgendaRegla> {
  return apiFetch<AgendaRegla>(
    `/v1/profesionales/${profesionalId}/agenda/reglas`,
    { method: "POST", body: payload },
  );
}

export function updateRegla(
  profesionalId: number,
  reglaId: number,
  payload: AgendaReglaUpdate,
): Promise<AgendaRegla> {
  return apiFetch<AgendaRegla>(
    `/v1/profesionales/${profesionalId}/agenda/reglas/${reglaId}`,
    { method: "PATCH", body: payload },
  );
}

export function deleteRegla(
  profesionalId: number,
  reglaId: number,
): Promise<void> {
  return apiFetch<void>(
    `/v1/profesionales/${profesionalId}/agenda/reglas/${reglaId}`,
    { method: "DELETE" },
  );
}

// ---------- Bloqueos ----------

export function listBloqueos(
  profesionalId: number,
  desde?: string,
): Promise<AgendaBloqueo[]> {
  const qs = desde ? `?desde=${desde}` : "";
  return apiFetch<AgendaBloqueo[]>(
    `/v1/profesionales/${profesionalId}/agenda/bloqueos${qs}`,
  );
}

export function createBloqueo(
  profesionalId: number,
  payload: AgendaBloqueoCreate,
): Promise<AgendaBloqueo> {
  return apiFetch<AgendaBloqueo>(
    `/v1/profesionales/${profesionalId}/agenda/bloqueos`,
    { method: "POST", body: payload },
  );
}

export function deleteBloqueo(
  profesionalId: number,
  bloqueoId: number,
): Promise<void> {
  return apiFetch<void>(
    `/v1/profesionales/${profesionalId}/agenda/bloqueos/${bloqueoId}`,
    { method: "DELETE" },
  );
}

// ---------- Slots ----------

export function listSlotsLibres(
  profesionalId: number,
  fecha: string,
  sedeId?: number | null,
): Promise<SlotLibre[]> {
  const params = new URLSearchParams({ fecha });
  if (sedeId) params.set("sede_id", String(sedeId));
  return apiFetch<SlotLibre[]>(
    `/v1/profesionales/${profesionalId}/agenda/slots?${params.toString()}`,
  );
}

export const DIAS_SEMANA = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
] as const;
